import { DEV_ONLY } from '../env.js';

// The parts of the brief an advisor cannot take out of the client's copy, or
// cannot reword (Kristen Grubba and Danielle Martin, 2026-10-27): what Pure
// tells clients they receive has to be there, even when it only says a point
// does not apply yet. Which parts those are is decided later with compliance
// and Jake (Jimmy, 2026-10-27), so the list starts empty and only the
// mechanism is in place.
//
//   'required'  stays in the client's copy; the advisor can still edit the words
//   'fixed'     stays, in Pure's words: no edits either
//
// Keys:
//   'goals'          the Goals section of the goals page
//   'opening'        the letter and the situation under the figures
//   'theme'          the theme section, when the rules give one
//   'area:<code>'    every point in a planning area (cash_flow, tax, investments, insurance, estate)
//   'topic:<key>'    one point, by its rule key ("topic:tax.rmdTimer")
//   'next_steps'     the next steps of a follow-up
//
// A suggestion the advisor added is theirs to take out again, whatever its area.
export const LOCKS = Object.freeze({});

const RANK = { required: 1, fixed: 2 };

/**
 * On dev and the local server only, `?locks=theme,area:tax=fixed` tries locks
 * out before the real list exists. Each entry is a key, optionally `=fixed`.
 */
export function locksFromUrl(search) {
  if (!DEV_ONLY) return null;
  const raw = new URLSearchParams(search || '').get('locks');
  if (!raw) return null;
  const out = {};
  for (const part of raw.split(',')) {
    const [key, level] = part.trim().split('=');
    if (key) out[key] = level === 'fixed' ? 'fixed' : 'required';
  }
  return out;
}

/** The locks in force: the list above, or a dev trial from the address bar. */
export function activeLocks() {
  if (typeof window === 'undefined') return LOCKS;
  return locksFromUrl(window.location.search) || LOCKS;
}

/** The strongest lock among the keys, or null. */
export function lockOf(locks, ...keys) {
  let best = null;
  for (const k of keys) {
    const l = locks?.[k];
    if (RANK[l] && (!best || RANK[l] > RANK[best])) best = l;
  }
  return best;
}

/** Whether the goals page's Goals section must stay on the page. */
export function goalsLocked() {
  return Boolean(lockOf(activeLocks(), 'goals'));
}

/** The lock on one point of the brief: its own, or its area's. Never on an added suggestion. */
export function topicLock(locks, item) {
  if (!item || item.from === 'suggestion') return null;
  return lockOf(locks, `topic:${item.key}`, `area:${item.area}`);
}
